import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Space, Switch } from 'antd';
import {
  grantPredefinedLocationConsent,
  revokePredefinedLocationConsent,
} from '../../../store/actions/carriers';

const PredefinedLocationAccessCell = ({
  recipient,
  consents,
  predefinedType,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [actionLoading, setActionLoading] = useState(null);
  const pendingToggleRef = useRef(new Set());

  const handleGrant = (recipientCompanyId, toggleKey) => {
    if (!toggleKey || pendingToggleRef.current.has(toggleKey)) {
      return Promise.resolve();
    }
    pendingToggleRef.current.add(toggleKey);
    setActionLoading(`grant-${recipientCompanyId}`);
    return Promise.resolve(dispatch(grantPredefinedLocationConsent(recipientCompanyId)))
      .finally(() => {
        setActionLoading(null);
        pendingToggleRef.current.delete(toggleKey);
      });
  };

  const handleRevoke = (consentId, toggleKey) => {
    if (!toggleKey || pendingToggleRef.current.has(toggleKey)) {
      return Promise.resolve();
    }
    pendingToggleRef.current.add(toggleKey);
    setActionLoading(`revoke-${consentId}`);
    return Promise.resolve(dispatch(revokePredefinedLocationConsent(consentId)))
      .finally(() => {
        setActionLoading(null);
        pendingToggleRef.current.delete(toggleKey);
      });
  };

  const predefinedConsent = consents
    && consents.find((consent) => consent.type === predefinedType);
  const consentId = predefinedConsent?.id;
  const hasConsent = Boolean(predefinedConsent);
  const eligibleTypes = ['PERMANENT_VEHICLE_MONITORING', 'LONG_TIME'];
  const hasEligibleType = consents
    && consents.some((consent) => eligibleTypes.includes(consent.type));

  if (!hasEligibleType) {
    return null;
  }

  const toggleKey = recipient?.id
    || recipient?.taxNumber
    || recipient?.name
    || consentId
    || 'unknown';
  const isLoading = actionLoading === `revoke-${consentId}`
    || actionLoading === `grant-${recipient?.id}`;
  const isDisabled = (hasConsent ? !consentId : !recipient?.id)
    || pendingToggleRef.current.has(toggleKey);

  return (
    <Space size="small">
      <Switch
        checked={hasConsent}
        loading={isLoading}
        disabled={isDisabled}
        onChange={(checked) => (
          checked
            ? handleGrant(recipient?.id, toggleKey)
            : handleRevoke(consentId, toggleKey)
        )}
      />
      {!isDisabled && (
        <span>
          {hasConsent ? t('CARRIERS.ACCESS_GRANTED') : t('CARRIERS.ACCESS_NOT_GRANTED')}
        </span>
      )}
    </Space>
  );
};

PredefinedLocationAccessCell.propTypes = {
  recipient: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    taxNumber: PropTypes.string,
  }),
  consents: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    createdAt: PropTypes.string,
  })),
  predefinedType: PropTypes.string.isRequired,
};

PredefinedLocationAccessCell.defaultProps = {
  recipient: null,
  consents: [],
};

export default PredefinedLocationAccessCell;
